import { Component, OnInit, Inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RegelDetailsService, IRegelDetails, RegelDetails, LeverancierDetails, IRegelLeverancierFeature, RegelLeverancierFeature, IRegelPrijsRegel, SoftwareSource } from '../regel-details.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AutocompleteService, IFust, ISelectItem, IVbnFeatureValue, IVbnFeatureType, VbnFeatureValue, VbnFeatureType } from '../autocomplete.service';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { debounceTime, tap, map, switchMap, startWith, finalize } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { FromVbnS02Pipe } from '../from-vbn-s02.pipe';
import { LeverancierStatus } from '../regels.service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ContainsStringPipe } from '../containsstring.pipe';
import { IsNullOrEmptyPipe } from '../isnullorempty.pipe';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

@Component({
    selector: 'app-regel-details',
    templateUrl: './regel-details.component.html',
    styleUrls: ['./regel-details.component.css']
})
export class RegelDetailsComponent implements OnInit {
    readonly MAX_PRIJSREGELS = 6;
    id: string;
    details: IRegelDetails = new RegelDetails();
    isLoadingDetails: boolean = false;
    isDisabled: boolean = false;
    isButtonDisabled: boolean = false;
    showBeladingValidationMessage: boolean = false;
    isAlternativeItem = false;

    selectedFeatureType?: string;
    selectedFeatureValue?: string;
    featureTypes: IVbnFeatureType[] = [];
    filteredFeatureTypes: Observable<IVbnFeatureType[]>;
    filteredFeatureValues: Observable<IVbnFeatureValue[]>;
    featureValues: IVbnFeatureValue[] = [];
    featureTypeControl: FormControl = new FormControl('');
    featureValueControl: FormControl = new FormControl('');
    isLoadingFeatureTypesAndValues: boolean;

    public LeverancierStatus = LeverancierStatus;
    public SoftwareSource = SoftwareSource;

    //fusten
    fustNr1Control = new FormControl('');
    fustNr2Control = new FormControl('');
    fustNr3Control = new FormControl('');

    filteredFusten1!: Observable<any[]>;
    filteredFusten2!: Observable<any[]>;
    filteredFusten3!: Observable<any[]>;

    fusten: IFust[] = [];
    selectedFust1: IFust;
    selectedFust2: IFust;
    selectedFust3: IFust;
    isLoadingFusten = false;

    //hoogte
    heightControl: FormControl = new FormControl('');
    controlSupplierAssignedId: FormControl = new FormControl('');
    omschrijvingControl: FormControl = new FormControl('', [Validators.required]);
    actualPriceAftuinControl: FormControl = new FormControl('');
    actualPriceBoxControl: FormControl = new FormControl('');
    heights: IHeight[] = [];
    filteredHeights: Observable<IHeight[]>;
    filteredAssignedIds: Observable<ISelectItem[]>;
    selectedHeight?: number;
    isLoadingSupplierAssignedIds: boolean;
    dooshoogtes: number[] = [];

    // Default prijsregel for single-price mode (PeriodFrom/To/TierQuantity = null)
    defaultPrijsRegel?: IRegelPrijsRegel;

    // Track original values of prijsregels to detect changes on blur
    private prijsRegelOriginals: { [id: number]: { actualPriceAftuin?: number; actualPriceBox?: number } } = {};

    constructor(
        private _dialog: MatDialog,
        public dialogRef: MatDialogRef<RegelDetailsComponent>,
        private alternativeDialogRef: MatDialog,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private fb: FormBuilder,
        private _autocompleteService: AutocompleteService,
        private _detailsService: RegelDetailsService,
        private _snackBar: MatSnackBar) { }

    ngOnInit() {

        this.isLoadingDetails = true;

        this._autocompleteService.getDooshoogtes('')
            .subscribe((data: number[]) => this.dooshoogtes = data);

        // Initialize filtered observables
        this.filteredFusten1 = this.fustNr1Control.valueChanges.pipe(
            startWith(''),
            map(value => this.filterFusten(value))
        );

        this.filteredFusten2 = this.fustNr2Control.valueChanges.pipe(
            startWith(''),
            map(value => this.filterFusten(value))
        );

        this.filteredFusten3 = this.fustNr3Control.valueChanges.pipe(
            startWith(''),
            map(value => this.filterFusten(value))
        );

        this._autocompleteService.getFusten('', true)
            .subscribe((fusten: IFust[]) => {
                this.fusten = fusten;
            });

        this._detailsService.get(this.data.id)
            .subscribe(data => {
                if (!data.leverancierDetails.supplierAssignedId)
                    data.leverancierDetails.supplierAssignedId = data.supplierAssignedId;
                this.setAanvraagRegelDetails(data);
            });
    }

    private filterFusten(searchValue: string | number): any[] {
        if (!searchValue) {
            return this.fusten;
        }

        const filterValue = String(searchValue).toLowerCase();
        return this.fusten.filter(fust =>
            fust.id.toString().includes(filterValue) ||
            fust.naam.toLowerCase().includes(filterValue) ||
            (fust.alfa && fust.alfa.toLowerCase().includes(filterValue))
        );
    }

    private setAanvraagRegelDetails(data: IRegelDetails) {
        this.isLoadingDetails = false;
        this.details = data;
        this.isDisabled = data.leverancierDetails.confirmationDate != null;

        // Initialize originals for prijsregels (price lines)
        if (this.details) {
            if (this.details.prijsRegels.length) {
                this.details.prijsRegels.forEach(p => {
                    this.prijsRegelOriginals[p.id] = {
                        actualPriceAftuin: p.actualPriceAftuin,
                        actualPriceBox: p.actualPriceBox
                    };
                });
            }
            // has no regels but should show at least one regel
            else if (this.details.prijsperiodeAndStaffelsEnabled && (this.details.hasTieredPricing || this.details.hasPurchasePricePeriods)) {
                this.addEmptyPrijsRegelAuto();
            }
        }

        // Always select the default prijsregel for fixed-price mode binding.
        this.defaultPrijsRegel = this.getDefaultPrijsRegel();

        if (this.details.leverancierDetails.fustNr1) {
            var fust = this.fusten.find(f => f.id == this.details.leverancierDetails.fustNr1);
            if (fust) {
                this.selectedFust1 = fust;
            }
            else {
                this._autocompleteService.getFust(this.details.leverancierDetails.fustNr1)
                    .subscribe(data => {
                        if (data) {
                            this.selectedFust1 = data;
                        }
                    });
            }
        }
        if (this.details.leverancierDetails.fustNr2) {
            var fust = this.fusten.find(f => f.id == this.details.leverancierDetails.fustNr2);
            if (fust) {
                this.selectedFust2 = fust;
            }
            else {
                this._autocompleteService.getFust(this.details.leverancierDetails.fustNr2)
                    .subscribe(data => {
                        if (data) {
                            this.selectedFust2 = data;
                        }
                    });
            }
        }
        if (this.details.leverancierDetails.fustNr3) {
            var fust = this.fusten.find(f => f.id == this.details.leverancierDetails.fustNr3);
            if (fust) {
                this.selectedFust3 = fust;
            }
            else {
                this._autocompleteService.getFust(this.details.leverancierDetails.fustNr3)
                    .subscribe(data => {
                        if (data) {
                            this.selectedFust3 = data;
                        }
                    });
            }
        }

        // set featureType of regel
        this.filteredFeatureTypes = this.featureTypeControl.valueChanges.pipe(
            startWith(''),
            map(value => {
                console.log(value);
                this.featureValueControl.reset();
                this._loadVbnFeatureValues();
                return this._filterFeatureTypes(value || '');
            }),
        );

        // set featureValue of regel
        this.filteredFeatureValues = this.featureValueControl.valueChanges.pipe(
            startWith(''),
            map(value => this._filterFeatureValues(value || '')),
        );

        // set height of regel
        this.filteredHeights = this.heightControl.valueChanges.pipe(
            startWith(''),
            map(value => this._filterHeights(value || '')),
        );

        this.filteredAssignedIds = this.controlSupplierAssignedId.valueChanges.pipe(
            startWith(''),
            debounceTime(300),
            tap(() => this.isLoadingSupplierAssignedIds = true),
            switchMap((value: string) => this._autocompleteService.getSupplierAssignedIDs(value, this.details.kwekerEan)
                .pipe(
                    map(val => val && val.data),
                    finalize(() => this.isLoadingSupplierAssignedIds = false),
                )),
            /*tap((val) => console.log(val)),*/
        );

        this.omschrijvingControl.patchValue(this.details.leverancierDetails.omschrijving);
        this.omschrijvingControl.valueChanges.subscribe(value => this.details.leverancierDetails.omschrijving = value);

        // Fixed-price mode validators depend on verlading type.
        this.actualPriceAftuinControl.setValidators(this.details.showPrijsAftuin ? [Validators.required] : []);
        this.actualPriceBoxControl.setValidators(this.details.showPrijsHamiplantGeleverd ? [Validators.required] : []);
        this.actualPriceAftuinControl.updateValueAndValidity({ emitEvent: false });
        this.actualPriceBoxControl.updateValueAndValidity({ emitEvent: false });

        // Bind fixed-price inputs to the default prijsregel (when present).
        this.actualPriceAftuinControl.patchValue(this.defaultPrijsRegel?.actualPriceAftuin ?? '', { emitEvent: false });
        this.actualPriceAftuinControl.valueChanges.subscribe(value => {
            if (this.defaultPrijsRegel) {
                this.defaultPrijsRegel.actualPriceAftuin = value;
            }
        });

        this.actualPriceBoxControl.patchValue(this.defaultPrijsRegel?.actualPriceBox ?? '', { emitEvent: false });
        this.actualPriceBoxControl.valueChanges.subscribe(value => {
            if (this.defaultPrijsRegel) {
                this.defaultPrijsRegel.actualPriceBox = value;
            }
        });

        var fromVbnS02Pipe = new FromVbnS02Pipe();
        this._autocompleteService.getVbnFeatureValues('S02').subscribe(data => {
            let heights: any = [];
            data.forEach(function (item) {
                let height: IHeight = new Height();

                height.id = item.id;
                height.featureType = item.featureTypeId;
                height.featureValue = item.featureValue;
                height.decimalValue = fromVbnS02Pipe.transform(item.featureValue);
                height.description = item.description;

                heights.push(height);
            });
            this.heights = heights;
            let leverancierHeight = this.details.leverancierDetails.hoogte;
            var selectedHeight = this.heights.find(function (element) {
                return element.decimalValue == leverancierHeight;
            });
            if (selectedHeight) {
                this.selectedHeight = selectedHeight.decimalValue;
                this.heightControl.patchValue(selectedHeight.description);
            }
            else {
                this.heightControl.patchValue('');
            }

            this.heightControl.validator = Validators.compose([
                Validators.required,
                FormCustomValidators.valueSelected(this.heights),
            ]);
        }, (err) => {
            this.isLoadingDetails = false;

            console.error("Failed! " + err);
            this._snackBar.open('Er is iets fout gegaan tijdens het ophalen van de details. Probeer opnieuw.', null, {
                duration: 2000,
                panelClass: ['kp-status-message', 'kp-status-message-error']
            });
        });

        this._autocompleteService.getVbnFeatureTypes().subscribe(data => {
            let featureTypes: any = [];
            data.forEach(function (item) {
                featureTypes.push(item);
            });
            this.featureTypes = featureTypes;

            this.featureTypeControl.validator = Validators.compose([
                Validators.required,
                FormCustomValidators.valueSelected(this.featureTypes),
            ]);
        }, (err) => {
            this.isLoadingDetails = false;

            console.error("Failed! " + err);
            this._snackBar.open('Er is iets fout gegaan tijdens het ophalen van de details. Probeer opnieuw.', null, {
                duration: 2000,
                panelClass: ['kp-status-message', 'kp-status-message-error']
            });
        });

        this._loadVbnFeatureValues();

        this.evaluateButtonDisability();
    }

    private getDefaultPrijsRegel(): IRegelPrijsRegel | undefined {
        const regels = this.details?.prijsRegels ?? [];
        if (!regels.length) return undefined;

        const isNull = (v: any) => v === null || v === undefined;
        return regels.find(p => isNull(p.periodFrom) && isNull(p.periodTo) && isNull(p.tierQuantity)) ?? regels[0];
    }

    // Triggered when a prijsregel input loses focus; checks for changes and saves via API
    onPrijsRegelBlur(p: IRegelPrijsRegel, field: 'actualPriceAftuin' | 'actualPriceBox') {
        if (this.isDisabled) return;
        if (!this.details) return;
        // Section is only visible when there are price periods or tiered pricing
        if (!(this.details.hasTieredPricing || this.details.hasPurchasePricePeriods)) return;

        const orig = this.prijsRegelOriginals[p.id] || { actualPriceAftuin: undefined, actualPriceBox: undefined };
        const newVal = p[field];
        const oldVal = orig[field];

        // Normalize numbers: treat null/undefined/NaN as undefined
        const norm = (v: any) => (v === null || v === undefined || v === '' || isNaN(Number(v)) ? undefined : Number(v));
        const nNew = norm(newVal);
        const nOld = norm(oldVal);

        const changed = (Number.isNaN(nNew) && !Number.isNaN(nOld)) || (nNew !== nOld);
        if (!changed) return;

        if (p.id && p.id > 0) {
            // Existing -> update
            if (p.softwareSource === SoftwareSource.KwekerPortaal && !!p.periodFrom && !!p.periodTo && this.hasOverlap(p)) {
                this._snackBar.open('De periodes overlappen met een andere prijsregel.', undefined, {
                    duration: 2500,
                    panelClass: ['kp-status-message', 'kp-status-message-error']
                });
                return;
            }

            this._detailsService.updatePrijsRegel(p)
                .subscribe(() => {
                    // Update originals after successful save
                    this.prijsRegelOriginals[p.id] = {
                        actualPriceAftuin: norm(p.actualPriceAftuin),
                        actualPriceBox: norm(p.actualPriceBox)
                    };
                    this._snackBar.open('Prijsregel opgeslagen', undefined, {
                        duration: 1500,
                        panelClass: ['kp-status-message', 'kp-status-message-success']
                    });
                }, err => {
                    console.error('Failed to save prijsregel', err);
                    this._snackBar.open('Opslaan van prijsregel mislukt. Probeer opnieuw.', undefined, {
                        duration: 2000,
                        panelClass: ['kp-status-message', 'kp-status-message-error']
                    });
                });
        } else {
            // New (temporary) -> only create when mandatory fields are present
            const hasPeriods = !this.details.hasPurchasePricePeriods || (!!p.periodFrom && !!p.periodTo);
            if (p.softwareSource === SoftwareSource.KwekerPortaal && !hasPeriods) {
                // Don't show snackbar here, user might just be filling prices first, 
                // but we can't save yet without periods if it's KwekerPortaal.
                return;
            }

            if (p.softwareSource === SoftwareSource.KwekerPortaal && hasPeriods && this.hasOverlap(p)) {
                this._snackBar.open('De periodes overlappen met een andere prijsregel.', undefined, {
                    duration: 2500,
                    panelClass: ['kp-status-message', 'kp-status-message-error']
                });
                return;
            }

            this._detailsService.addPrijsRegel(p).subscribe(res => {
                // replace temp object with server one
                const idx = this.details.prijsRegels?.indexOf(p) ?? -1;
                if (idx >= 0 && this.details.prijsRegels) {
                    this.details.prijsRegels[idx] = res;
                }
                // Initialize originals for the new object
                this.prijsRegelOriginals[res.id] = {
                    actualPriceAftuin: norm(res.actualPriceAftuin),
                    actualPriceBox: norm(res.actualPriceBox)
                };
                this._snackBar.open('Prijsregel toegevoegd', undefined, {
                    duration: 1500,
                    panelClass: ['kp-status-message', 'kp-status-message-success']
                });
            }, err => {
                console.error('Failed to add prijsregel', err);
                this._snackBar.open('Toevoegen van prijsregel mislukt. Controleer de verplichte velden.', undefined, {
                    duration: 2500,
                    panelClass: ['kp-status-message', 'kp-status-message-error']
                });
            });
        }
    }

    // Fixed-price mode: persist changes on the default prijsregel.
    onFixedPrijsBlur(field: 'actualPriceAftuin' | 'actualPriceBox') {
        if (this.isDisabled) return;
        if (!this.details) return;
        // Only applicable in single-price mode
        if (this.details.hasTieredPricing || this.details.hasPurchasePricePeriods) return;

        const p = this.defaultPrijsRegel;
        if (!p || !p.id || p.id <= 0) return;

        const orig = this.prijsRegelOriginals[p.id] || { actualPriceAftuin: undefined, actualPriceBox: undefined };
        const newVal = p[field];
        const oldVal = orig[field];

        const norm = (v: any) => (v === null || v === undefined || v === '' || isNaN(Number(v)) ? undefined : Number(v));
        const nNew = norm(newVal);
        const nOld = norm(oldVal);

        const changed = (Number.isNaN(nNew) && !Number.isNaN(nOld)) || (nNew !== nOld);
        if (!changed) return;

        this._detailsService.updatePrijsRegel(p)
            .subscribe(() => {
                this.prijsRegelOriginals[p.id] = {
                    actualPriceAftuin: norm(p.actualPriceAftuin),
                    actualPriceBox: norm(p.actualPriceBox)
                };
                this._snackBar.open('Prijs opgeslagen', undefined, {
                    duration: 1500,
                    panelClass: ['kp-status-message', 'kp-status-message-success']
                });
                this.evaluateButtonDisability();
            }, err => {
                console.error('Failed to save fixed price', err);
                this._snackBar.open('Opslaan van prijs mislukt. Probeer opnieuw.', undefined, {
                    duration: 2000,
                    panelClass: ['kp-status-message', 'kp-status-message-error']
                });
                this.evaluateButtonDisability();
            });
    }

    // Save full KwekerPortaal regel when editable fields change (period/tier/prices)
    onKwekerPortaalRegelBlur(p: IRegelPrijsRegel) {
        if (this.isDisabled) return;
        if (p.softwareSource !== SoftwareSource.KwekerPortaal) return;
        // Require both periods for KwekerPortaal
        const hasPeriods = !this.details.hasPurchasePricePeriods || (!!p.periodFrom && !!p.periodTo);

        if (hasPeriods && this.hasOverlap(p)) {
            this._snackBar.open('De periodes overlappen met een andere prijsregel.', undefined, {
                duration: 2500,
                panelClass: ['kp-status-message', 'kp-status-message-error']
            });
            return;
        }

        if (hasPeriods && p.periodFrom && p.periodTo && new Date(p.periodFrom) > new Date(p.periodTo)) {
            this._snackBar.open('De "Periode van" datum mag niet na de "Periode tot" datum liggen.', undefined, {
                duration: 2500,
                panelClass: ['kp-status-message', 'kp-status-message-error']
            });
            return;
        }

        if (p.id && p.id > 0) {
            // Existing -> update
            if (p.softwareSource === SoftwareSource.KwekerPortaal && !!p.periodFrom && !!p.periodTo && this.hasOverlap(p)) {
                this._snackBar.open('De periodes overlappen met een andere prijsregel.', undefined, {
                    duration: 2500,
                    panelClass: ['kp-status-message', 'kp-status-message-error']
                });
                return;
            }

            this._detailsService.updatePrijsRegel(p).subscribe(() => {
                this._snackBar.open('Prijsregel opgeslagen', undefined, {
                    duration: 1500,
                    panelClass: ['kp-status-message', 'kp-status-message-success']
                });
            }, err => {
                console.error('Failed to save prijsregel', err);
                this._snackBar.open('Opslaan van prijsregel mislukt. Probeer opnieuw.', undefined, {
                    duration: 2000,
                    panelClass: ['kp-status-message', 'kp-status-message-error']
                });
            });
        } else {
            // New (temporary) -> only create when mandatory fields are present
            if (!hasPeriods) {
                this._snackBar.open('Beide periodes zijn verplicht voor KwekerPortaal prijsregels.', undefined, {
                    duration: 2500,
                    panelClass: ['kp-status-message', 'kp-status-message-error']
                });
                return;
            }
            this._detailsService.addPrijsRegel(p).subscribe(res => {
                // replace temp object with server one
                const idx = this.details.prijsRegels?.indexOf(p) ?? -1;
                if (idx >= 0 && this.details.prijsRegels) {
                    this.details.prijsRegels[idx] = res;
                }
                this._snackBar.open('Prijsregel toegevoegd', undefined, {
                    duration: 1500,
                    panelClass: ['kp-status-message', 'kp-status-message-success']
                });
            }, err => {
                console.error('Failed to add prijsregel', err);
                this._snackBar.open('Toevoegen van prijsregel mislukt. Controleer de verplichte velden.', undefined, {
                    duration: 2500,
                    panelClass: ['kp-status-message', 'kp-status-message-error']
                });
            });
        }
    }

    addEmptyPrijsRegel(type: 'periode' | 'staffel') {
        if (!this.details) return;
        const nieuw: IRegelPrijsRegel = {
            id: 0,
            sourceId: undefined,
            softwareSource: SoftwareSource.KwekerPortaal,
            offerteAanvraagRegelMetaId: this.details.id,
            periodFrom: type === 'periode' ? (this.details.deadlineLeverancierDate ? new Date(this.details.deadlineLeverancierDate) : undefined) : undefined,
            periodTo: type === 'periode' ? (this.details.deadlineLeverancierDate ? new Date(this.details.deadlineLeverancierDate) : undefined) : undefined,
            tierQuantity: type === 'staffel' ? (this.details.aantal ?? 0) : undefined,
            desiredPriceAftuin: undefined,
            actualPriceAftuin: undefined,
            desiredPriceBox: undefined,
            actualPriceBox: undefined,
            createdDate: new Date(),
            modifiedDate: undefined
        };

        // Add locally as empty row; will be created on first valid blur
        this.details.prijsRegels = this.details.prijsRegels || [];
        this.details.prijsRegels.push(nieuw);
    }

    // Eén plus-knop: maakt een nieuwe lege prijsregel aan op basis van de bestaande ngIf-condities
    // - Als alleen periodes actief zijn -> zet periodFrom/To
    // - Als alleen staffels actief zijn -> zet tierQuantity = 1
    // - Als beide actief zijn -> zet zowel periods als tierQuantity = 1
    addEmptyPrijsRegelAuto() {
        if (!this.details) return;

        // Respecteer maximum aantal prijsregels
        const huidigeCount = this.details.prijsRegels?.length || 0;
        if (huidigeCount >= this.MAX_PRIJSREGELS) {
            this._snackBar.open('Maximaal 6 prijsregels mogelijk', undefined, {
                duration: 2000,
                panelClass: ['kp-status-message', 'kp-status-message-warning']
            });
            return;
        }

        const usePeriods = !!this.details.hasPurchasePricePeriods;
        const useTier = !!this.details.hasTieredPricing;

        let defaultPeriodFrom = this.details.deadlineLeverancierDate ? new Date(this.details.deadlineLeverancierDate) : undefined;
        let defaultPeriodTo = this.details.deadlineLeverancierDate ? new Date(this.details.deadlineLeverancierDate) : undefined;
        let defaultTier = this.details.aantal ?? 1;

        if (this.details.prijsRegels && this.details.prijsRegels.length > 0) {
            if (usePeriods) {
                // Vind de prijsregel met de hoogste periodTo
                const lastPeriodRegel = this.details.prijsRegels
                    .filter(p => !!p.periodTo)
                    .reduce((maxRegel, current) => {
                        if (!maxRegel) return current;
                        return new Date(current.periodTo!) > new Date(maxRegel.periodTo!) ? current : maxRegel;
                    }, undefined as IRegelPrijsRegel | undefined);

                if (lastPeriodRegel) {
                    defaultPeriodFrom = lastPeriodRegel.periodFrom ? new Date(lastPeriodRegel.periodFrom) : undefined;
                    defaultPeriodTo = lastPeriodRegel.periodTo ? new Date(lastPeriodRegel.periodTo) : undefined;
                }
            }
        }

        const nieuw: IRegelPrijsRegel = {
            id: 0,
            sourceId: undefined,
            softwareSource: SoftwareSource.KwekerPortaal,
            offerteAanvraagRegelMetaId: this.details.id,
            periodFrom: usePeriods ? defaultPeriodFrom : undefined,
            periodTo: usePeriods ? defaultPeriodTo : undefined,
            tierQuantity: useTier ? defaultTier : undefined,
            desiredPriceAftuin: undefined,
            actualPriceAftuin: undefined,
            desiredPriceBox: undefined,
            actualPriceBox: undefined,
            createdDate: new Date(),
            modifiedDate: undefined
        };

        this.details.prijsRegels = this.details.prijsRegels || [];
        this.details.prijsRegels.push(nieuw);
    }

    private hasOverlap(p: IRegelPrijsRegel): boolean {
        if (!this.details?.prijsRegels) return false;

        const otherRegels = this.details.prijsRegels.filter(x =>
            x !== p &&
            x.softwareSource === SoftwareSource.KwekerPortaal &&
            !!x.periodFrom && !!x.periodTo
        );

        for (const other of otherRegels) {
            const pFrom = new Date(p.periodFrom!);
            const pTo = new Date(p.periodTo!);
            const oFrom = new Date(other.periodFrom!);
            const oTo = new Date(other.periodTo!);

            // Overlap check: (StartA <= EndB) and (EndA >= StartB)
            if (pFrom <= oTo && pTo >= oFrom) {
                return true;
            }
        }

        return false;
    }

    deletePrijsRegel(p: IRegelPrijsRegel) {
        if (this.isDisabled) return;
        if (p.softwareSource !== SoftwareSource.KwekerPortaal) return;
        if (!p.id || p.id === 0) {
            // Unsaved temp regel, just remove locally
            if (this.details?.prijsRegels) {
                this.details.prijsRegels = this.details.prijsRegels.filter(x => x !== p);
            }
            return;
        }
        const dialogRef = this._dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Bevestigen',
                message: 'Weet u zeker dat u deze prijsregel wilt verwijderen?'
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result === true) {
                this._detailsService.deletePrijsRegel(p.id).subscribe(() => {
                    if (this.details?.prijsRegels) {
                        this.details.prijsRegels = this.details.prijsRegels.filter(x => x.id !== p.id);
                    }
                    this._snackBar.open('Prijsregel verwijderd', undefined, {
                        duration: 1500,
                        panelClass: ['kp-status-message', 'kp-status-message-success']
                    });
                }, err => {
                    console.error('Failed to delete prijsregel', err);
                    this._snackBar.open('Verwijderen van prijsregel mislukt.', undefined, {
                        duration: 2000,
                        panelClass: ['kp-status-message', 'kp-status-message-error']
                    });
                });
            }
        });
    }

    // Helpers voor template
    get prijsRegelsCount(): number {
        return this.details?.prijsRegels?.length || 0;
    }

    get isMaxPrijsRegelsReached(): boolean {
        return this.prijsRegelsCount >= this.MAX_PRIJSREGELS;
    }

    get hasPersistedDefaultPrijsRegel(): boolean {
        return !!this.defaultPrijsRegel && this.defaultPrijsRegel.id > 0;
    }

    private _loadVbnFeatureValues(): void {
        this.featureValues = [];
        if (!this.selectedFeatureType) {
            return;
        }

        this._autocompleteService.getVbnFeatureValues(this.selectedFeatureType).subscribe(data => {
            let featureValues: any = [];
            data.forEach(function (item) {
                featureValues.push(item);
            });
            this.featureValues = featureValues;

            this.featureValueControl.validator = Validators.compose([
                Validators.required,
                FormCustomValidators.valueSelected(this.featureValues),
            ]);

            this.featureValueControl.reset();
        }, (err) => {
            this.isLoadingDetails = false;

            console.error("Failed! " + err);
            this._snackBar.open('Er is iets fout gegaan tijdens het ophalen van de details. Probeer opnieuw.', null, {
                duration: 2000,
                panelClass: ['kp-status-message', 'kp-status-message-error']
            });
        });
    }

    private _filterFeatureTypes(value: string): IVbnFeatureType[] {
        if (!value) value = '';
        if (value === '') return this.featureTypes;
        const filterValue = this._normalizeValue(value);
        return this.featureTypes.filter(x => this._normalizeValue(x.id).includes(filterValue) || this._normalizeValue(x.description).includes(filterValue) || x.id == filterValue);
    }

    private _filterFeatureValues(value: string): IVbnFeatureValue[] {
        if (!value) value = '';
        if (value === '') return this.featureValues;
        const filterValue = this._normalizeValue(value);
        return this.featureValues.filter(x => this._normalizeValue(x.featureValue).includes(filterValue) || this._normalizeValue(x.description).includes(filterValue) || x.featureValue == filterValue);
    }

    private _filterHeights(value: string): IHeight[] {
        if (!value) value = '';
        if (value === '') return this.heights;
        const filterValue = this._normalizeValue(value);
        return this.heights.filter(x => this._normalizeValue(x.description).includes(filterValue) || x.featureValue == filterValue);
    }

    displayFustFn(fustId: number | null): string {
        if (!fustId) {
            return '';
        }

        const selectedFust = this.fusten.find(f => f.id === fustId);
        if (!selectedFust) {
            return '';
        }

        const alfa = selectedFust.alfa && selectedFust.alfa.trim() !== ''
            ? ` (${selectedFust.alfa})`
            : '';

        return `${selectedFust.id}. ${selectedFust.naam}${alfa}`;
    }

    onChangeFustNr() {
        if (this.details.leverancierDetails.fustNr1) {
            let fust = this.fusten.filter(x => x.id == this.details.leverancierDetails.fustNr1);
            if (fust.length) {
                this.selectedFust1 = fust[0];
            }
        }

        if (this.details.leverancierDetails.fustNr2) {
            let fust = this.fusten.filter(x => x.id == this.details.leverancierDetails.fustNr2);
            if (fust.length) {
                this.selectedFust2 = fust[0];
            }
        }

        if (this.details.leverancierDetails.fustNr3) {
            let fust = this.fusten.filter(x => x.id == this.details.leverancierDetails.fustNr3);
            if (fust.length) {
                this.selectedFust3 = fust[0];
            }
        }
    }

    private _normalizeValue(value: string): string {
        if (!value) value = '';
        if (typeof value === 'string') {
            return value.toLowerCase().replace(/\s/g, '');
        }

        return value;
    }

    displayFn(fust: IFust) {
        if (fust) {
            var name = `${fust.id}. ${fust.naam}`;
            if (fust.alfa) {
                name += ` (${fust.alfa})`;
            }
            return name;
        }
    }

    supplierAssignedIdSelected(event: MatAutocompleteSelectedEvent) {
        if (event.option && event.option.value) {
            this._detailsService.getSupply(event.option.value, this.details.kwekerEan).subscribe(supply => {
                // naam, vbn-code, potmaat, planthoogte, belading 
                this.details.leverancierDetails.omschrijving = supply.description;
                this.details.leverancierDetails.potmaat = supply.potmaat;
                this.details.photos = supply.images;

                this.details.leverancierDetails.hoogte = supply.hoogte;
                this.details.leverancierDetails.fustNr1 = supply.boxType;

                if (this.details.leverancierDetails.hoogte) this.heightControl.setValue(this.details.leverancierDetails.hoogte + " cm");

                if (supply.boxType) {
                    this._autocompleteService.getFust(supply.boxType).subscribe((fust) => {
                        this.details.leverancierDetails.fustNr1 = fust.id;
                    });
                }

                this.details.leverancierDetails.aantalLagenPerLadingdrager1 = supply.numberOfLayersPerCarrier;
                this.details.leverancierDetails.fustenPerLaag1 = supply.numberOfBoxesPerLayer;
                this.details.leverancierDetails.stuks1 = supply.numberOfUnitsPerBox;

                this.evaluateButtonDisability();
            });
        }
    }

    openNewAanvraagRegel(id: string) {
        this.isAlternativeItem = true;

        var alternativeItem = {
            ...this.details,
            photos: [],
            leverancierDetails: {
                kwekerEan: this.details.kwekerEan,
                parentId: id,
                //alternatieveOfferteAanvraagRegelId: this.details.sourceId,
                offerteAanvraagId: this.details.offerteAanvraagId,
                geldigheidKwekersaanbiedingTot: this.details.leverancierDetails.geldigheidKwekersaanbiedingTot,
                potmaat: this.details.potmaat,
                aantal: this.details.aantal,
                aantallenInAanvraagType: this.details.aantallenInAanvraagType
            },
        };

        this.setAanvraagRegelDetails(alternativeItem);
    }

    onEnterFeatureType(evt: any) {
        this.selectedFeatureType = evt.source.value;
    }

    onEnterFeatureValue(evt: any) {
        this.selectedFeatureValue = evt.source.value;
    }

    onEnter(evt: any) {
        const selectedHeight = this.heights.find(height =>
            height.decimalValue == evt.source.value);
        if (evt.source.selected) {
            if (selectedHeight) {
                this.details.leverancierDetails.hoogte = selectedHeight.decimalValue;
                this.heightControl.patchValue(selectedHeight.description);
            }
        }
    }

    onChangeDooshoogte(event: any) {
        var newDooshoogte = event.source.value;
        if (newDooshoogte && newDooshoogte != 0) {
            // calculate number of dozen per layer
            var numberOfLayers = this.details.maxHoogteLadingdrager1 / newDooshoogte;
            if (numberOfLayers) {
                this.details.leverancierDetails.aantalLagenPerLadingdrager1 = Math.floor(numberOfLayers);
            }
        }

        this.evaluateButtonDisability();
    }

    onChangeGeldigheidKwekersaanbieding(event: any) {
        let date = event.target.value;
        let dateElements = date.split('-');
        if (dateElements == null || dateElements == undefined || dateElements.length != 3 || dateElements[0].length == 4) {
            return;
        }

        let formattedDate: Date = null;
        if (dateElements[2].length == 4) {
            // dd-MM-yyyy
            formattedDate = new Date(dateElements[2] + '-' + dateElements[1] + '-' + dateElements[0]);
        }

        if (formattedDate != null) {
            this.details.leverancierDetails.geldigheidKwekersaanbiedingTot = formattedDate;
        }
    }

    showDooshoogteValidationMessage() {
        const containsStringPipe = new ContainsStringPipe();
        let showDooshoogte = containsStringPipe.transform(this.details.ladingdragerType1, 'pallet') || containsStringPipe.transform(this.details.ladingdragerType2, 'pallet') || containsStringPipe.transform(this.details.ladingdragerType3, 'pallet');

        return showDooshoogte && !this.details.leverancierDetails.dooshoogte;
    }

    doShowBeladingValidationMessage(ladingdragerType?: string, fustNr?: number, aantalLagenPerLadingdrager?: number, fustenPerLaag?: number, stuks?: number) {
        const isNullOrEmptyPipe = new IsNullOrEmptyPipe();

        return !isNullOrEmptyPipe.transform(ladingdragerType as any) && (fustNr == null || aantalLagenPerLadingdrager == null || fustenPerLaag == null || stuks == null);
    }

    evaluateButtonDisability() {
        this.isButtonDisabled = this.showDooshoogteValidationMessage();

        this.showBeladingValidationMessage =
            this.doShowBeladingValidationMessage(this.details.leverancierDetails.ladingdragerType1 as any, this.details.leverancierDetails.fustNr1, this.details.leverancierDetails.aantalLagenPerLadingdrager1, this.details.leverancierDetails.fustenPerLaag1, this.details.leverancierDetails.stuks1)
            ||
            this.doShowBeladingValidationMessage(this.details.leverancierDetails.ladingdragerType2 as any, this.details.leverancierDetails.fustNr2, this.details.leverancierDetails.aantalLagenPerLadingdrager2, this.details.leverancierDetails.fustenPerLaag2, this.details.leverancierDetails.stuks2)
            ||
            this.doShowBeladingValidationMessage(this.details.leverancierDetails.ladingdragerType3 as any, this.details.leverancierDetails.fustNr3, this.details.leverancierDetails.aantalLagenPerLadingdrager3, this.details.leverancierDetails.fustenPerLaag3, this.details.leverancierDetails.stuks3);

        this.isButtonDisabled ||= this.showBeladingValidationMessage;

        if (this.details.hasTieredPricing || this.details.hasPurchasePricePeriods) {
            if (this.details.prijsRegels && this.details.prijsRegels.length > 0) {
                const hasMissingPrice = this.details.prijsRegels.some(p => {
                    const missingAftuin = this.details.showPrijsAftuin && (p.actualPriceAftuin === null || p.actualPriceAftuin === undefined);
                    const missingBox = this.details.showPrijsHamiplantGeleverd && (p.actualPriceBox === null || p.actualPriceBox === undefined);
                    return missingAftuin || missingBox;
                });

                const hasOverlap = this.details.prijsRegels.some(p => p.softwareSource === SoftwareSource.KwekerPortaal && !!p.periodFrom && !!p.periodTo && this.hasOverlap(p));
                const hasInvalidOrder = this.details.prijsRegels.some(p => p.softwareSource === SoftwareSource.KwekerPortaal && !!p.periodFrom && !!p.periodTo && new Date(p.periodFrom) > new Date(p.periodTo));

                this.isButtonDisabled ||= hasMissingPrice || hasOverlap || hasInvalidOrder;
            } else {
                // Als er geen prijsregels zijn terwijl ze wel enabled zijn, beschouwen we dit ook als invalid voor voltooien
                this.isButtonDisabled = true;
            }
        } else {
            // Fixed single-price mode: re-evaluate default prijsregel before validating.
            this.defaultPrijsRegel = this.getDefaultPrijsRegel();
            if (!this.defaultPrijsRegel) {
                this.isButtonDisabled = true;
            } else {
                this.isButtonDisabled ||= (this.details.showPrijsAftuin && !this.actualPriceAftuinControl.valid) ||
                                          (this.details.showPrijsHamiplantGeleverd && !this.actualPriceBoxControl.valid);
            }
        }
    }

    saveRegelDetails(leverancierStatus: LeverancierStatus) {
        this.evaluateButtonDisability();

        if (leverancierStatus === LeverancierStatus.Voltooid && this.isButtonDisabled) {
            const hasOverlap = this.details.prijsRegels?.some(p => p.softwareSource === SoftwareSource.KwekerPortaal && !!p.periodFrom && !!p.periodTo && this.hasOverlap(p));
            const hasInvalidOrder = this.details.prijsRegels?.some(p => p.softwareSource === SoftwareSource.KwekerPortaal && !!p.periodFrom && !!p.periodTo && new Date(p.periodFrom) > new Date(p.periodTo));
            const missingDefaultRow = !(this.details.hasTieredPricing || this.details.hasPurchasePricePeriods) && !this.getDefaultPrijsRegel();

            if (hasOverlap) {
                this._snackBar.open('Er zijn prijsregels met overlappende periodes.', null, {
                    duration: 3000,
                    panelClass: ['kp-status-message', 'kp-status-message-error']
                });
            } else if (hasInvalidOrder) {
                this._snackBar.open('Er zijn prijsregels waarbij de startdatum na de einddatum ligt.', null, {
                    duration: 3000,
                    panelClass: ['kp-status-message', 'kp-status-message-error']
                });
            } else {
                var message = this.showDooshoogteValidationMessage()
                    ? 'De dooshoogte is verplicht voordat u kunt voltooien.'
                    : this.showBeladingValidationMessage
                        ? 'De belading is verplicht voordat u kunt voltooien.'
                        : missingDefaultRow
                            ? 'De standaard prijsregel ontbreekt. Vernieuw de pagina en probeer opnieuw.'
                        : 'De prijzen zijn verplicht voordat u kunt voltooien.';

                this._snackBar.open(message, null, {
                    duration: 3000,
                    panelClass: ['kp-status-message', 'kp-status-message-error']
                });
            }
            return;
        }

        if ((leverancierStatus == LeverancierStatus.NietLeverbaarUitverkocht && !this.isDisabled) || (!this.isButtonDisabled && !this.isDisabled && !this.heightControl.invalid)) {
            this.details.leverancierDetails.status = LeverancierStatus.Voltooid;
            this._detailsService.updateMeta(this.details.leverancierDetails)
                .subscribe(data => {
                    this._snackBar.open("Data opgeslagen", null, {
                        duration: 2000,
                        panelClass: ['kp-status-message', 'kp-status-message-success']
                    });
                    this.dialogRef.close(
                        {
                            refreshAllRows: this.isAlternativeItem,
                            result: true,
                            status: leverancierStatus,
                            item: this.details
                        });
                }, (err) => {
                    console.error("Failed! " + err);
                    this._snackBar.open('Er is iets fout gegaan tijdens het opslaan van de details. Probeer opnieuw.', null, {
                        duration: 2000,
                        panelClass: ['kp-status-message', 'kp-status-message-error']
                    });
                });
        }
    }

    onAddFeature() {
        var feature: IRegelLeverancierFeature = {
            offerteAanvraagRegelId: this.details.id,
            type: this.selectedFeatureType,
            value: this.selectedFeatureValue
        };

        console.log(feature);
        console.log(this.selectedFeatureType);
        console.log(this.selectedFeatureValue);

        this._detailsService.addFeature(feature)
            .subscribe((data: IRegelLeverancierFeature) => {
                this.featureTypeControl.reset();
                this.featureValueControl.reset();

                this.featureTypeControl.updateValueAndValidity();
                this.featureValueControl.updateValueAndValidity();

                this.selectedFeatureType = undefined;
                this.selectedFeatureValue = undefined;
                console.log(this.selectedFeatureType);
                console.log(this.selectedFeatureValue);

                // get all features for this regel details
                this._detailsService.getFeatures(this.details.id).subscribe((data: IRegelLeverancierFeature[]) => {
                    this.details.features = data;
                },
                    (err) => {
                        console.error("Failed! " + err);
                        this._snackBar.open('Er is iets fout gegaan tijdens het verversen van de kenmerken. Probeer opnieuw.', null, {
                            duration: 2000,
                            panelClass: ['kp-status-message', 'kp-status-message-error']
                        });
                    });
            },
                (err) => {
                    console.error("Failed! " + err);
                    this._snackBar.open('Er is iets fout gegaan tijdens het toevoegen van het kenmerk. Probeer opnieuw.', null, {
                        duration: 2000,
                        panelClass: ['kp-status-message', 'kp-status-message-error']
                    });
                });
    }

    onDeleteFeature(feature: IRegelLeverancierFeature): void {
        console.log(feature);

        const message = 'Weet u zeker dat u dit kenmerk wilt verwijderen?';

        const confirmDialog = this._dialog.open(ConfirmDialogComponent, {
            data: { title: 'Bevestigen', message },
        });

        confirmDialog.afterClosed().subscribe(result => {
            if (result === true) {
                this._detailsService.archiveFeature(this.details.leverancierDetails, feature)
                    .subscribe(data => {
                        var index = this.details.features.indexOf(feature);
                        if (index > -1) {
                            // should always be the case
                            this.details.features.splice(index, 1);
                        }
                    },
                        (err) => {
                            console.error("Failed! " + err);
                            this._snackBar.open('Er is iets fout gegaan tijdens het verwijderen van het kenmerk. Probeer opnieuw.', null, {
                                duration: 2000,
                                panelClass: ['kp-status-message', 'kp-status-message-error']
                            });
                        });
            }
        });
    }
}

export class FormCustomValidators {
    static valueSelected(myArray: any[]): ValidatorFn {
        return (c: AbstractControl): { [key: string]: boolean } | null => {
            let selectboxValue = c.value;
            let pickedOrNot = myArray.filter(
                (alias) => alias.description === selectboxValue || alias.featureValue == selectboxValue
            );

            if (pickedOrNot.length > 0) {
                // everything's fine. return no error. therefore it's null.
                return null;
            } else {
                //there's no matching selectboxvalue selected. so return match error.
                return { match: true };
            }
        };
    }
}

export class Height implements IHeight {
    id: number;
    featureType: string;
    featureValue: string;
    decimalValue?: number;
    description: string;
}

export interface IHeight {
    id: number;
    featureType: string;
    featureValue: string;
    decimalValue?: number;
    description: string;
}
